import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { CircularProgress } from '@mui/material'

import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest'
import FileCopyIcon from '@mui/icons-material/FileCopy'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import MergeIcon from '@mui/icons-material/Merge'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'

import { theme } from '../../themes/theme'
import { BoldMedBodyText } from '../styles/Text'
import { categoryKey, loadingStatuses } from '../../util/categoryConsts'

const loading = {
  bgColor: 'rgba(255, 172, 71, 0.1)',
  borderColor: 'rgba(255, 172, 71, 1)',
  iconColor: 'rgba(255, 172, 71, 1)'
}

const ready = {
  bgColor: 'rgba(16, 208, 112, 0.1)',
  borderColor: 'rgba(16, 208, 112, 1)',
  iconColor: 'rgba(16, 208, 112, 1)'
}

const statusConfig = {
  RAW: {
    icon: InsertDriveFileIcon,
    bgColor: 'rgba(143, 163, 187, 0.1)',
    borderColor: 'rgba(143, 163, 187, 1)',
    iconColor: 'rgba(51, 51, 51, 1)'
  },
  CONVERTING: { icon: PictureAsPdfIcon, ...loading },
  CONVERTED: { icon: PictureAsPdfIcon, ...ready },
  DETERMINING_DUPLICATES: { icon: FileCopyIcon, ...loading },
  DUPLICATES_READY: { icon: FileCopyIcon, ...ready },
  COMBINING_FILES: { icon: MergeIcon, ...loading },
  COMBINED: { icon: MergeIcon, ...ready },
  SUGGESTING_REDACTIONS: { icon: SettingsSuggestIcon, ...loading },
  REDACTIONS_NOTED: { icon: SettingsSuggestIcon, ...ready },
  SAVING_REDACTIONS: { icon: SettingsSuggestIcon, ...loading },
  FINALIZING_REDACTIONS: { icon: FactCheckIcon, ...loading },
  REDACTIONS_FINALIZED: { icon: FactCheckIcon, ...ready },
  ADDED_TO_RESPONSE: {
    icon: CheckCircleIcon,
    bgColor: 'rgba(16, 192, 208, 0.1)',
    borderColor: 'rgba(16, 192, 208, 1)',
    iconColor: 'rgba(16, 192, 208, 1)'
  }
}

const Background = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: fit-content;
  border: ${props => `1px solid ${statusConfig[props.status].borderColor}`};
  background-color: ${props => statusConfig[props.status].bgColor};
  border-radius: ${theme.borderRadius.button};
  padding: ${props => (props.iconOnly ? '5px 13px' : '5px 15px')};
`

const WhiteBackground = styled.div`
  width: fit-content;
  background-color: #fff;
  border-radius: ${theme.borderRadius.button};
`

const Status = styled(BoldMedBodyText)`
  text-transform: capitalize;
`

const StyledIcon = styled.div`
  display: flex;
  color: ${props => props.iconColor};

  .MuiSvgIcon-root {
    height: 22px;
    width: 22px;
  }
`

const Spinner = styled(CircularProgress)`
  margin: 2px;
  color: ${props => props.color};
`

function CategoryStatus({ status, iconOnly = false }) {
  const showLoadingCircle = !iconOnly && loadingStatuses.includes(status)
  const IconComponent = statusConfig[status].icon

  return (
    <WhiteBackground>
      <Background status={status} iconOnly={iconOnly}>
        {showLoadingCircle ? (
          <Spinner color={statusConfig[status].borderColor} size={18} />
        ) : (
          <StyledIcon iconColor={statusConfig[status].iconColor}>
            <IconComponent />
          </StyledIcon>
        )}
        {!iconOnly && <Status>{categoryKey[status].label}</Status>}
      </Background>
    </WhiteBackground>
  )
}

CategoryStatus.propTypes = {
  status: PropTypes.oneOf([
    'RAW',
    'CONVERTING',
    'CONVERTED',
    'DETERMINING_DUPLICATES',
    'DUPLICATES_READY',
    'COMBINING_FILES',
    'COMBINED',
    'SUGGESTING_REDACTIONS',
    'REDACTIONS_NOTED',
    'FINALIZING_REDACTIONS',
    'REDACTIONS_FINALIZED',
    'ADDED_TO_RESPONSE'
  ]).isRequired,
  iconOnly: PropTypes.bool
}

export default CategoryStatus
